import React, { useState, useEffect } from 'react';
import {
    Clock,
    UserCheck,
    UserPlus,
    MessageSquare,
    AlertCircle,
    Filter,
    Search,
    Calendar,
    Settings,
    Building2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const ActivityLog = () => {
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        type: ''
    });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    useEffect(() => {
        fetchActivityLog();
        setCurrentPage(1);
    }, [filters]);

    // Pagination calculations
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentActivities = activities.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(activities.length / itemsPerPage);

    const fetchActivityLog = async () => {
        try {
            setLoading(true);
            const query = `month=${filters.month}&year=${filters.year}${filters.type ? `&type=${filters.type}` : ''}`;
            const { data } = await api.get(`/analytics/activity-log?${query}`);

            if (data.success) {
                setActivities(data.data);
            }
        } catch (error) {
            console.error('Error fetching activity log:', error);
            toast.error('Failed to load activity log');
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'login': return <UserCheck className="text-green-600" size={20} />;
            case 'join': return <UserPlus className="text-blue-600" size={20} />;
            case 'post': return <MessageSquare className="text-indigo-600" size={20} />;
            case 'ticket': return <AlertCircle className="text-amber-600" size={20} />;
            case 'system': return <Settings className="text-indigo-600" size={20} />;
            case 'college': return <Building2 className="text-teal-600" size={20} />;
            default: return <Clock className="text-gray-400" size={20} />;
        }
    };

    const getBadgeStyle = (type) => {
        switch (type) {
            case 'login': return 'bg-green-100 text-green-700';
            case 'join': return 'bg-blue-100 text-blue-700';
            case 'post': return 'bg-indigo-100 text-indigo-700';
            case 'ticket': return 'bg-amber-100 text-amber-700';
            case 'system': return 'bg-indigo-100 text-indigo-700';
            case 'college': return 'bg-teal-100 text-teal-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 lg:p-6 space-y-6">
            {/* Header */}
            <div className="glass rounded-xl shadow-md border border-white/30 p-4 mb-6 animate-fade-in">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-bold gradient-text mb-1 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-indigo-500" />
                            System Activity Log
                        </h1>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="font-medium">Track comprehensive system events and user actions</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap gap-3 items-center w-full">
                <div className="flex items-center gap-2 sm:border-r border-gray-200 sm:pr-4 w-full sm:w-auto">
                    <Filter size={18} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Filters</span>
                </div>

                <select
                    value={filters.month}
                    onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 w-full sm:w-auto"
                >
                    {months.map((m, idx) => (
                        <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                </select>

                <select
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 w-full sm:w-auto"
                >
                    {years.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>

                <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 w-full sm:w-auto sm:min-w-[150px]"
                >
                    <option value="">All Activity Types</option>
                    <option value="login">User Logins</option>
                    <option value="join">New Registrations</option>
                    <option value="post">Community Posts</option>
                    <option value="ticket">Support Tickets</option>
                    <option value="system">System Actions</option>
                    <option value="college">College Actions</option>
                </select>

                <div className="w-full sm:w-auto sm:ml-auto text-sm text-gray-500 flex items-center justify-center sm:justify-end gap-2 mt-1 sm:mt-0">
                    <Calendar size={14} />
                    <span>Viewing {months[filters.month - 1]} {filters.year}</span>
                </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Loading activity log...</p>
                    </div>
                ) : activities.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                        {currentActivities.map((item, idx) => (
                            <div
                                key={idx}
                                className="p-4 hover:bg-gray-50 transition-colors flex items-start gap-4 group"
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getBadgeStyle(item.type).replace('text-', 'bg-opacity-20 ')}`}>
                                    {getIcon(item.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                                            {item.title}
                                        </h3>
                                        <span className="text-xs text-gray-500 tabular-nums shrink-0">
                                            {new Date(item.timestamp).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium">
                                        {item.description}
                                    </p>
                                    {item.meta && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {Object.entries(item.meta).map(([key, val]) => val && (
                                                <span key={key} className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600 border border-gray-200">
                                                    {key}: <span className="font-semibold text-gray-800">{val}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Clock size={24} className="text-gray-300" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">No activity found</h3>
                        <p className="text-gray-500">Try adjusting your filters to see more results.</p>
                    </div>
                )}

                {/* Pagination Block */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="text-xs text-gray-500 text-center sm:text-left">
                            Page {currentPage} of {totalPages} • Showing {currentActivities.length} of {activities.length} activities
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>

                            <div className="flex items-center space-x-1">
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${currentPage === pageNum
                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                                } ${currentPage !== pageNum && (pageNum < currentPage - 1 || pageNum > currentPage + 1) ? 'hidden sm:inline-block' : 'inline-block'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-4 text-center text-xs text-gray-400">
                End of results for {months[filters.month - 1]} {filters.year}
            </div>
        </div>
    );
};

export default ActivityLog;
